import { FilterValueType } from './../../filtered-search/filtered-search.component';
import { tap, debounceTime, distinctUntilChanged, map, pairwise, filter, catchError, finalize } from 'rxjs/operators';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subscription, of, throwError, zip } from 'rxjs';
import { fixDate } from '../../pipes/fix-date/fix-date.pipe';
import { Metadata } from 'src/app/hickory-api';
import { AuthService } from '../../authentication/auth.service';
import { Filter, FilterField } from '../../filtered-search/filtered-search.component';
import { ApiError } from '../../model/utility';

export type FancyRowClass = 'soft-red';

interface DataSourceConfig<TRequest, TResponse, TModel extends object> {
  requestTransformer: () => TRequest;
  request: (request: TRequest) => Observable<TResponse>;
  isEqual: (previous: TRequest, current: TRequest) => boolean;
  responseMapper?: (response: TResponse) => TModel[];
  errorMessage?: string;
  forceRefresh?: boolean;
}

export type HasMetadata = { metadata?: Metadata };
export interface HasDataSource<TModel extends object, TService, TRequest> {
  dataSource: BaseDataSource<TModel, TService, TRequest>;
  updateDataSourceField: (field: string, value: FilterValueType) => void;
}

export type TableColumnLink<T> = (
  row: T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryParams?: Record<string, string | any[]> | null | undefined,
  redirect?: string | string[] | null | undefined,
  onclick?: TableColumnButton<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => Record<string, any>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type TableColumnButton<T> = () => void;

export interface TableColumn<T extends object> {
  field: string;
  localizationKey?: string;
  render?: (row: T) => unknown;
  // obsolete
  filterable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  link?: TableColumnLink<T>;
  style?: { [key: string]: string };
  useRedirect?: boolean;
  useQueryParams?: boolean;
  noSort?: boolean;
  tooltip?: (row: T) => string;
  icon?: (row: T) => string;
  iconTooltip?: (row: T) => string;
  locked?: boolean;
  permissions?: string[];
  sticky?: boolean;
  hideByDefault?: boolean;
}

export class TableColumnRenders {
  public static readonly FromMetadata = <T extends HasMetadata>(row: T, field: string) => {
    const split = field.split('.');

    if (!split) {
      return '';
    }

    if (!row.metadata) {
      return '';
    }

    let value: object = row.metadata;
    for (const s of split) {
      if (!value) {
        return '';
      }

      value = value[s as keyof typeof value];
    }

    return value;
  };

  public static readonly Reduce = <T extends object>(row: T, field: string): string => {
    if (row?.[field as keyof T]) {
      return row[field as keyof T] as string;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return (field?.split('.').reduce((o: object, i) => o?.[i as keyof typeof o], row) as unknown as string) ?? '';
  };

  public static readonly FixDate = <T extends object>(
    row: T,
    field: keyof T | string,
    format?: Intl.DateTimeFormatOptions,
  ) => {
    const date = TableColumnRenders.Reduce(row, field as string);
    if (!date) {
      return '';
    }

    const fixedDate = fixDate(date);
    if (!fixedDate) {
      return '';
    }

    return new Date(fixedDate).toLocaleString(
      'en-US',
      format ?? {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      },
    );
  };

  public static readonly Date = <T extends object>(row: T, field: string) => {
    const value = TableColumnRenders.Reduce(row, field);
    if (!value) {
      return '';
    }

    return new Date(value as string | number | Date).toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  };

  public static readonly USDFormatter = (precision?: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      ...(precision ? { minimumFractionDigits: 2, maximumFractionDigits: precision } : {}),
    });

  public static readonly Currency = <T extends object>(row: T, field: string, precision?: number) => {
    const value = TableColumnRenders.Reduce(row, field);
    let amount = 0;
    if (value) {
      amount = parseFloat(value);
    }
    return `${TableColumnRenders.USDFormatter(precision).format(amount)}`;
  };

  public static readonly Numerical = <T extends object>(row: T, field: string, _?: number) => {
    const value = TableColumnRenders.Reduce(row, field);
    let amount = 0;
    if (value) {
      amount = parseFloat(value);
    }
    return amount.toLocaleString();
  };

  public static readonly DisplayArray = <T extends object>(row: T, field: string) => {
    const reduced = TableColumnRenders.Reduce(row, field);
    if (!Array.isArray(reduced)) {
      return undefined;
    }

    return reduced?.map((x: T & { display: string }) => x.display)?.join(', ');
  };

  public static readonly DisplayArrayField = <T extends object>(row: T, field: string, arrayField: string) => {
    const reduced = TableColumnRenders.Reduce(row, field);
    if (!Array.isArray(reduced)) {
      return undefined;
    }

    return (
      reduced
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        ?.map((x) => x[arrayField])
        ?.join(', ')
    );
  };
}

export function createFiltersFromParams(
  params: { [key: string]: unknown },
  availableFields: FilterField[],
  authService: AuthService,
  dateField: string = 'daterange',
): Filter[] {
  const filters: Filter[] = [];
  for (const [field, value] of Object.entries(params)) {
    const filter = availableFields.find(
      (f) => f.field === field && (authService?.hasAllPermissions(f.permissions ?? []) || true),
    );

    if (filter) {
      const filterValue = filter.type === 'boolean' ? value == 'true' : (value as boolean);
      filters.push({ field: field, value: filterValue });
    }
  }

  // this should probably be done better
  const { timestampStart, timestampEnd } = params;
  if (timestampStart || timestampEnd) {
    const dateFilter = <Filter>{
      field: dateField,
      value: {
        start: timestampStart ? new Date(timestampStart as Date) : null,
        end: timestampEnd ? new Date(timestampEnd as Date) : null,
      },
    };

    filters.push(dateFilter);
  }

  return filters;
}

export abstract class BaseDataSource<TModel extends object, TService, TRequest> extends DataSource<TModel> {
  protected readonly dataSubject = new BehaviorSubject<TModel[] | null>(null);
  protected readonly loadingSubject = new BehaviorSubject<boolean>(false);
  protected readonly currentRequest = new BehaviorSubject<TRequest>(null as unknown as TRequest);
  protected readonly columnsChanged = new BehaviorSubject<boolean>(false);
  protected abstract readonly columns: TableColumn<TModel>[];
  protected hasInit = false;

  protected hasError = new BehaviorSubject<boolean>(false);
  protected errorMessage = new BehaviorSubject<string | null>(null);

  public readonly columnsChanged$ = this.columnsChanged.asObservable();
  public readonly currentRequest$ = this.currentRequest.asObservable();
  public readonly searchTerm$ = new BehaviorSubject<string | null>(null);
  public readonly loadDataTerm$ = new BehaviorSubject<boolean>(false);

  public itemCount = 0;
  protected readonly subscriptions = new Subscription();

  protected get pageIndex() {
    return this.paginator?.pageIndex ?? 0;
  }

  protected get pageSize() {
    return this.paginator?.pageSize ?? 10;
  }

  protected get sortField(): string {
    if (this.sort?.direction) {
      return this.sort.active;
    }

    return '';
  }

  protected get sortDirection() {
    if (!this.sort) {
      return '';
    }

    switch (this.sort.direction) {
      case 'desc':
        return '-';
      case 'asc':
      default:
        return '';
    }
  }

  public get error$(): Observable<string | null> {
    if (!this.hasError.getValue()) {
      return of(null);
    }

    return this.errorMessage.asObservable();
  }

  abstract get availableColumns(): TableColumn<TModel>[];
  abstract get queryArguments(): object;

  get displayedColumns(): string[] {
    return this.availableColumns.map((v) => v.field) || [];
  }

  get search(): string | null {
    return this.searchTerm$.getValue();
  }

  get data(): TModel[] | null {
    return this.dataSubject.getValue();
  }

  get loading$(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  get isReady(): boolean {
    return this.hasInit;
  }

  get empty$(): Observable<boolean | null> {
    return zip(
      this.dataSubject.pipe(map((data) => data?.length === 0)),
      this.loadingSubject.pipe(map((loading) => loading)),
      this.error$.pipe(map((error) => !!error)),
    ).pipe(map(([empty, loading, hasError]) => empty && !loading && !hasError));
  }

  constructor(
    protected service: TService,
    public paginator: MatPaginator,
    public sort: MatSort,
    protected request: TRequest | object | undefined,
    protected loadOnInit: boolean = true,
  ) {
    super();

    if (request) {
      this.currentRequest.next({ ...(request as TRequest) });
    }

    if (loadOnInit) {
      this.init();
    }
  }

  connect(_: CollectionViewer): Observable<TModel[] | ReadonlyArray<TModel>> {
    return this.dataSubject.asObservable() as Observable<TModel[] | ReadonlyArray<TModel>>;
  }

  disconnect(_: CollectionViewer): void {
    this.dataSubject.complete();
  }

  /**
   * Create a subscription that will trigger a loadData call when the subject changes. By default, this will
   * reset the paginator and sort if they are present.
   * @param subject The behavior subject to subscribe to
   * @param forceRefresh Whether to send the forceRefresh to LoadData.
   * @param refreshPage Whether to reset the paginator to the first page
   * @returns The Subscription to the subject
   */
  protected createSubscription(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    source: Observable<any>,
    forceRefresh: boolean = false,
    refreshPage: boolean = true,
  ): Subscription {
    return source
      .pipe(
        pairwise(),
        filter(([prev, curr]) => prev !== curr),
        map(([_, curr]) => curr),
        distinctUntilChanged(),
        tap(() => {
          if (this.hasInit && this.paginator && refreshPage) {
            this.paginator.pageIndex = 0;
          }

          this.loadDataTerm$.next(forceRefresh);
        }),
      )
      .subscribe();
  }

  init(setInit: boolean = true) {
    this.subscriptions.add(
      this.loadDataTerm$
        .pipe(
          debounceTime(100),
          tap((forceRefresh: boolean) => {
            if (!this.hasInit) {
              return;
            }

            this.loadData(forceRefresh);
          }),
        )
        .subscribe(),
    );

    if (this.paginator) {
      this.subscriptions.add(this.paginator.page.pipe(tap((_) => this.loadDataTerm$.next(false))).subscribe());
    }

    if (this.sort) {
      this.subscriptions.add(
        this.sort.sortChange
          .pipe(
            tap(() => {
              if (this.hasInit && this.paginator) {
                this.paginator.firstPage();
              }

              this.loadDataTerm$.next(false);
            }),
          )
          .subscribe(),
      );
    }

    this.subscriptions.add(
      this.searchTerm$
        .pipe(
          pairwise(),
          filter(([prev, curr]) => prev !== curr),
          debounceTime(200),
          distinctUntilChanged(),
          tap((_) => {
            if (this.hasInit && this.paginator) {
              this.paginator.firstPage();
            }

            this.loadDataTerm$.next(false);
          }),
        )
        .subscribe(),
    );

    if (setInit) {
      this.hasInit = true;
    }
  }

  public abstract loadData(forceRefresh: boolean): void;
  protected previousRequest: TRequest | null = null;
  public loadDataFromConfig<TResponse>(config: DataSourceConfig<TRequest, TResponse, TModel>) {
    if (!this.hasInit) {
      return;
    }

    const request: TRequest = config.requestTransformer();

    if (this.previousRequest && config.isEqual(this.previousRequest, request) && !config.forceRefresh) {
      return;
    }

    this.setNextRequest(request);
    this.loadingSubject.next(true);
    this.hasError.next(false);
    this.errorMessage.next(null);
    this.previousRequest = { ...request };

    this.handleResponse(
      config.request(request) as Observable<{ totalItemCount?: number; items?: TModel[] }>,
      config.errorMessage,
    ).subscribe();
  }

  public setNextRequest(request: TRequest) {
    const nextRequest = {
      ...(this.currentRequest.value ?? {}),
      ...(request ?? {}),
    } as TRequest;

    this.currentRequest.next(nextRequest);
  }

  protected handleResponse(
    obs: Observable<{ totalItemCount?: number; items?: TModel[] }>,
    errorMessage: string = 'There was an error loading the data.',
  ): Observable<{ totalItemCount?: number; items?: TModel[] }> {
    return obs.pipe(
      catchError((err: ApiError) => {
        this.hasError.next(true);
        this.errorMessage.next(errorMessage);
        this.dataSubject.next([]);
        return throwError(() => err);
      }),
      tap((response) => {
        this.itemCount = response.totalItemCount!;
        this.dataSubject.next(response.items);
      }),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  unsubscribe() {
    this.subscriptions.unsubscribe();
  }
}
